import React, { useState, useEffect } from 'react';
import { ChevronLeft, Trash2, UserPlus, BookOpen, X, Check } from 'lucide-react';
import { useToast } from '../hooks/useToast';
import API_URL from '../config/API_URL';

const PersonalRecordingDetailPage = () => {
  const [recording, setRecording] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mediaUrl, setMediaUrl] = useState(null);
  const [availableKids, setAvailableKids] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedKids, setSelectedKids] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  
  const { showToast, ToastContainer } = useToast();
  
  // Get recording ID from URL
  const recordingId = window.location.pathname.split('/').pop();

  // Fetch recording details
  useEffect(() => {
    const fetchRecording = async () => {
      try {
        const response = await fetch(`${API_URL}/api/personal-books/recording/${recordingId}`, {
          credentials: 'include'
        });
        
        if (!response.ok) throw new Error('Failed to fetch recording');
        
        const data = await response.json();
        setRecording(data);
        
        // Get presigned URL for viewing
        const urlResponse = await fetch(`${API_URL}/api/personal-books/recording/${recordingId}/view-url`, {
          credentials: 'include'
        });
        
        if (urlResponse.ok) {
          const urlData = await urlResponse.json();
          setMediaUrl(urlData.url);
        }
      } catch (error) {
        console.error('Error fetching recording:', error);
        showToast('Failed to load recording', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchRecording();
  }, [recordingId]);

  // Fetch available kids
  useEffect(() => {
    const fetchAvailableKids = async () => {
      try {
        const familiesResponse = await fetch(`${API_URL}/api/family/my-families`, {
          credentials: 'include'
        });
        
        const families = await familiesResponse.json();
        
        const allKids = [];
        for (const family of families) {
          const detailRes = await fetch(`${API_URL}/api/family/${family.id}`, {
            credentials: 'include'
          });
          const detail = await detailRes.json();
          
          if (detail.kids) {
            detail.kids.forEach(kid => {
              allKids.push({
                ...kid,
                familyName: detail.family_name,
                familyId: detail.id
              });
            });
          }
        }
        
        setAvailableKids(allKids);
      } catch (error) {
        console.error('Error fetching kids:', error);
      }
    };

    fetchAvailableKids();
  }, []);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this recording?')) return;
    
    setIsDeleting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/personal-books/delete-recording/${recordingId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to delete recording');
      
      showToast('Recording deleted successfully', 'success');
      window.location.href = '/grandparent-dashboard';
    } catch (error) {
      console.error('Error deleting recording:', error);
      showToast('Failed to delete recording', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAssignToKids = async () => {
    if (selectedKids.length === 0) {
      showToast('Please select at least one child', 'warning');
      return;
    }
    
    setIsAssigning(true);
    
    try {
      const response = await fetch(`${API_URL}/api/personal-books/recording/${recordingId}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ kid_ids: selectedKids })
      });
      
      if (!response.ok) throw new Error('Failed to assign recording');
      
      // Refresh recording data
      const updatedResponse = await fetch(`${API_URL}/api/personal-books/recording/${recordingId}`, {
        credentials: 'include'
      });
      const updatedData = await updatedResponse.json();
      setRecording(updatedData);
      
      setShowAssignModal(false);
      setSelectedKids([]);
      showToast('Recording assigned successfully!', 'success');
    } catch (error) {
      console.error('Error assigning recording:', error);
      showToast('Failed to assign recording', 'error');
    } finally {
      setIsAssigning(false);
    }
  };

  const toggleKidSelection = (kidId) => {
    setSelectedKids(prev => {
      if (prev.includes(kidId)) {
        return prev.filter(id => id !== kidId);
      } else {
        return [...prev, kidId];
      }
    });
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ fontSize: '1.25rem', color: '#6b7280' }}>Loading personal recording...</p>
      </div>
    );
  }

  if (!recording) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ fontSize: '1.25rem', color: '#dc2626' }}>Recording not found</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)' }}>
      <ToastContainer />
      
      {/* Header */}
      <div style={{ background: 'white', borderBottom: '1px solid #e5e7eb', padding: '1rem 1.5rem' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button 
              onClick={() => window.location.href = '/grandparent-dashboard'}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
            >
              <ChevronLeft size={24} />
            </button>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <BookOpen size={20} color="#10b981" />
                <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937' }}>
                  {recording.book?.title || 'Personal Recording'}
                </h1>
              </div>
              <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                by {recording.book?.author || 'Unknown'}
              </p>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={() => setShowAssignModal(true)}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <UserPlus size={20} /> Assign to Kids
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              style={{
                padding: '0.75rem 1.5rem',
                background: isDeleting ? '#fca5a5' : '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: isDeleting ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Trash2 size={20} /> {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '2rem 1.5rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '2rem' }}>
          {/* Left: Media Player */}
          <div>
            <div style={{ 
              background: 'white', 
              borderRadius: '0.75rem', 
              overflow: 'hidden',
              marginBottom: '1.5rem',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
            }}>
              {recording.recording_type === 'audio_only' ? (
                // Audio Player
                <div style={{ 
                  padding: '3rem', 
                  background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                  textAlign: 'center'
                }}>
                  {recording.book?.cover_image_url && (
                    <img 
                      src={recording.book.cover_image_url} 
                      alt={recording.book.title}
                      style={{
                        width: '200px',
                        height: '280px',
                        objectFit: 'cover',
                        borderRadius: '0.5rem',
                        margin: '0 auto 2rem',
                        boxShadow: '0 10px 25px -5px rgb(0 0 0 / 0.1)'
                      }}
                    />
                  )}
                  <div style={{
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: '#064e3b',
                    marginBottom: '1rem'
                  }}>
                    🎙️ Audio Recording
                  </div>
                  {mediaUrl ? (
                    <audio 
                      controls 
                      style={{ 
                        width: '100%', 
                        maxWidth: '400px',
                        marginTop: '1.5rem'
                      }}
                    >
                      <source src={mediaUrl} type="audio/webm" />
                      Your browser doesn't support the audio tag.
                    </audio>
                  ) : (
                    <p style={{ color: '#6b7280' }}>Audio unavailable</p>
                  )}
                </div>
              ) : (
                // Video Player
                mediaUrl ? (
                  <video 
                    controls 
                    style={{ width: '100%', maxHeight: '600px', background: '#000' }}
                  >
                    <source src={mediaUrl} type="video/webm" />
                    Your browser doesn't support the video tag.
                  </video>
                ) : (
                  <div style={{ 
                    padding: '4rem', 
                    textAlign: 'center', 
                    background: '#f9fafb',
                    color: '#6b7280'
                  }}>
                    <p>Video unavailable</p>
                  </div>
                )
              )}
            </div>

            {/* Recording Info */}
            <div style={{ 
              background: 'white', 
              borderRadius: '0.75rem', 
              padding: '1.5rem',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
            }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>Recording Details</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6b7280' }}>Book Type:</span>
                  <span style={{ 
                    fontWeight: '600',
                    color: '#10b981',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}>
                    <BookOpen size={16} /> Personal Book
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6b7280' }}>Duration:</span>
                  <span style={{ fontWeight: '600' }}>
                    {Math.floor(recording.duration_seconds / 60)}:{(recording.duration_seconds % 60).toString().padStart(2, '0')}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6b7280' }}>Type:</span>
                  <span style={{ fontWeight: '600' }}>
                    {recording.recording_type === 'audio_only' ? '🎙️ Audio Only' : '📹 Video'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6b7280' }}>Recorded:</span>
                  <span style={{ fontWeight: '600' }}>
                    {new Date(recording.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Assigned Kids */}
          <div>
            <div style={{ 
              background: 'white', 
              borderRadius: '0.75rem', 
              padding: '1.5rem',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
            }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
                Assigned To ({recording.kids?.length || 0})
              </h3>
              
              {recording.kids && recording.kids.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {recording.kids.map(kid => (
                    <div
                      key={kid.id}
                      style={{
                        padding: '1rem',
                        border: '1px solid #10b981',
                        borderRadius: '0.5rem',
                        background: '#f0fdf4'
                      }}
                    >
                      <div style={{ fontWeight: '600', color: '#064e3b', marginBottom: '0.25rem' }}>
                        {kid.name}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#047857' }}>
                        {kid.family_name}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ 
                  padding: '2rem', 
                  textAlign: 'center',
                  color: '#6b7280',
                  fontStyle: 'italic'
                }}>
                  Not assigned to any kids yet
                </div>
              )}
              
              <button
                onClick={() => setShowAssignModal(true)}
                style={{
                  width: '100%',
                  marginTop: '1rem',
                  padding: '0.75rem',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <UserPlus size={20} /> Assign to More Kids
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Assign Modal - same as before */}
      {showAssignModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '0.75rem',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '600' }}>Assign to Kids</h3>
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedKids([]);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
              {availableKids.length === 0 ? (
                <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No kids available</p>
              ) : (
                availableKids.map(kid => {
                  const isSelected = selectedKids.includes(kid.id);
                  const isAlreadyAssigned = recording.kids?.some(k => k.id === kid.id);
                  
                  return (
                    <label
                      key={kid.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.75rem',
                        border: `2px solid ${isAlreadyAssigned ? '#10b981' : isSelected ? '#10b981' : '#e5e7eb'}`,
                        borderRadius: '0.5rem',
                        cursor: isAlreadyAssigned ? 'not-allowed' : 'pointer',
                        background: isAlreadyAssigned ? '#f0fdf4' : isSelected ? '#f0fdf4' : 'white',
                        opacity: isAlreadyAssigned ? 0.6 : 1
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => !isAlreadyAssigned && toggleKidSelection(kid.id)}
                        disabled={isAlreadyAssigned}
                        style={{ cursor: isAlreadyAssigned ? 'not-allowed' : 'pointer' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', color: '#1f2937' }}>
                          {kid.first_name}
                          {isAlreadyAssigned && (
                            <span style={{ 
                              marginLeft: '0.5rem',
                              fontSize: '0.75rem',
                              color: '#10b981',
                              fontWeight: '500'
                            }}>
                              (Already assigned)
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{kid.familyName}</div>
                      </div>
                      {isAlreadyAssigned && <Check size={20} color="#10b981" />}
                    </label>
                  );
                })
              )}
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedKids([]);
                }}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: '#e5e7eb',
                  color: '#1f2937',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAssignToKids}
                disabled={selectedKids.length === 0 || isAssigning}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: selectedKids.length > 0 && !isAssigning ? '#10b981' : '#e5e7eb',
                  color: selectedKids.length > 0 && !isAssigning ? 'white' : '#9ca3af',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: selectedKids.length > 0 && !isAssigning ? 'pointer' : 'not-allowed'
                }}
              >
                {isAssigning ? 'Assigning...' : `Assign (${selectedKids.length})`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalRecordingDetailPage;